import React, { useEffect, useState } from "react";
import { Col, Row, Select } from "antd";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import styles from "./add-edit-grade-scales.module.css";
import CustomDrawer from "../../../../../../../../../common/custom-drawer/custom-drawer";
import CustomTextfield from "../../../../../../../../../common/input/custom-textfield/custom-textfield";
import CustomButton from "../../../../../../../../../common/custom-button/custom-button";
import { toast } from "react-toastify";
import CustomSelect from "../../../../../../../../../common/input/custom-select/custom-select";
import SVGIcon from "../../../../../../../../../constants/SVGIcon";
import { PlusOutlined } from "@ant-design/icons";
import { createGradeScales, updateGradeScales } from "../../../../../../../../../services/school_info";

const { Option } = Select;

const AddEditGradeScales = ({ open, onClose, data, levels }) => {
  const [loading, setLoading] = useState(false);
  const { control, getValues, reset, trigger } = useForm({
    mode: "onChange",
    defaultValues: {
      level_id: "",
      gradeSets: [
        {
          symbol: "",
          upper_limit: "",
          lower_limit: "",
        },
      ],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "gradeSets",
  });

  useEffect(() => {
    if (data?.id) {
      reset({
        level_id: data?.level_id,
        gradeSets: [
          {
            symbol: data?.symbol,
            upper_limit: data?.upper_limit,
            lower_limit: data?.lower_limit,
          },
        ],
      });
    } else {
      reset({
        level_id: "",
        gradeSets: [
          {
            symbol: "",
            upper_limit: "",
            lower_limit: "",
          },
        ],
      });
    }
  }, [data]);

  const handleSubmit = async () => {
    try {
      const isValid = await trigger();
      if (!isValid) {
        return;
      }

      const formValues = getValues();
      if (data?.id) {
        const payload = {
          ...formValues?.gradeSets[0],
          level_id: formValues?.level_id,
        };
        await updateGradeScales(data?.id, payload);
      } else {
          const payload = {
            level_ids: formValues?.level_id,
            scales: formValues?.gradeSets,
          };
        await createGradeScales(payload);
      }
      toast.success(`Grade Scaling ${data?.id ? "edited" : "created"} successfully!`);
      handleClose();
    } catch (err) {
      toast.error(err?.data?.message || `Failed to ${data?.id ? "edit" : "create"} grade`);
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <CustomDrawer
      title={`${data?.id ? 'Edit' : 'Add'} Grade Scaling for Level`}
      open={open}
      onClose={handleClose}
      className={styles.drawer}
      footer={
        <div className={styles.footerWrapper}>
          <CustomButton
            onClick={handleClose}
            title="Back"
            variant="outlined"
            className={styles.footerBackButton}
          />
          <CustomButton
            title={`${data?.id ? "Edit" : "Add"} Grade`}
            size="large"
            onClick={handleSubmit}
          />
        </div>
      }
      width="47vw"
      drawerStyles={{ body: { padding: "50px" } }}
    >
      <Row gutter={[0, 20]}>
        <Col span={24}>
          <Controller
            name="level_id"
            control={control}
            render={({
              field: { onChange, value },
              fieldState: { error },
            }) => (
              <CustomSelect
                id="level_id"
                label="Levels"
                showRequired
                labelColor="#171717"
                placeholder="--Select--"
                showSearch
                size="large"
                mode={data?.id ? "" : "multiple"}
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
                disabled={data?.id ? true : false}
              >
                {levels?.map((option, index) => (
                  <Option key={`${option?.id}-${index}`} value={option?.id}>
                    {option?.name}
                  </Option>
                ))}
              </CustomSelect>
            )}
          />
        </Col>
        
        {fields?.map((field, index) => (
          <>
            <Col span={data?.id ? 24 : 22}>
              <Row gutter={[16, 10]}>
                <Col span={8}>
                  <Controller
                    name={`gradeSets.${index}.symbol`}
                    control={control}
                    rules={{ required: true }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <CustomTextfield
                        id={`gradeSets.${index}.symbol`}
                        label="Grade Name"
                        showRequired
                        labelColor="#171717"
                        showSearch
                        size="large"
                        value={value}
                        placeholder="Enter Symbol"
                        onChange={onChange}
                        status={error ? "error" : ""}
                      />
                    )}
                  />
                </Col>
                <Col span={8}>
                  <Controller
                    name={`gradeSets.${index}.upper_limit`}
                    control={control}
                    rules={{ required: true }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <CustomTextfield
                        id={`gradeSets.${index}.upper_limit`}
                        label="Upper limit"
                        showRequired
                        labelColor="#171717"
                        showSearch
                        size="large"
                        type="number"
                        value={value}
                        placeholder="Enter Upper Limit"
                        onChange={onChange}
                        status={error ? "error" : ""}
                      />
                    )}
                  />
                </Col>
                <Col span={8}>
                  <Controller
                    name={`gradeSets.${index}.lower_limit`}
                    control={control}
                    rules={{ required: true }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <CustomTextfield
                        id={`gradeSets.${index}.lower_limit`}
                        label="Lower limit"
                        showRequired
                        labelColor="#171717"
                        showSearch
                        size="large"
                        type="number"
                        value={value}
                        placeholder="Enter Lower Limit"
                        onChange={onChange}
                        status={error ? "error" : ""}
                      />
                    )}
                  />
                </Col>
              </Row>
            </Col>
            {fields?.length > 1 && (
              <Col span={2} className={styles.deleteClassIconWrapper}>
                <SVGIcon
                  name={"delete"}
                  width={"20"}
                  height={"20"}
                  className={styles.deleteClassIcon}
                  stroke={"rgba(243, 17, 17, 1)"}
                  onClick={() => remove(index)}
                />
              </Col>
            )}
          </>
        ))}

        {!data?.id && (
          <Col span={24} className={styles.addGradeWrapper}>
            <CustomButton
              title="Add Grade Scale"
              icon={
                <div className={styles.iconCircle}>
                  <PlusOutlined />
                </div>
              }
              onClick={() => append({ symbol: "", upper_limit: "", lower_limit: "" })}
            />
          </Col>
        )}
      </Row>
    </CustomDrawer>
  );
};

export default AddEditGradeScales;
